import React, { useDebugValue, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import core from '@gin-betting/core';

import { CLASS_NAME_OF_DISABLED_SCROLL } from 'app-constants';
import { useCssClassToggleOnDocument, useUnmount } from 'hooks';
import { State } from 'types';
import { closeFullScreenStream } from '../../actions';
import { ASPECT_RATIO } from '../../constants';

const {
    viewport: {
        selectors: { makeViewportSelector },
    },
} = core;

interface Input {
    isFullscreenActive: boolean;
    isPinned: boolean;
}

interface Output {
    height: number;
    containerRef: React.RefObject<HTMLDivElement> | null;
    isSmallMedium: boolean;
}

const CLASS_NAME_OF_FULLSCREEN_THEATER = '__app-is-theater-in-fullscreen';

function useTheater({ isFullscreenActive, isPinned }: Input): Output {
    const dispatch = useDispatch();

    const containerRef = useRef<HTMLDivElement>(null);
    const [widthBody, setWidthBody] = useState<number>(0);

    useEffect(() => {
        if (isPinned) {
            setWidthBody(containerRef.current?.offsetWidth ?? 0);
        }
    }, [isPinned]);

    const height = widthBody * ASPECT_RATIO;

    useEffect(() => {
        const handleKeydown = (event: KeyboardEvent) => {
            if (event.keyCode === 27 && isFullscreenActive) {
                dispatch(closeFullScreenStream());
            }
        };

        document.addEventListener('keydown', handleKeydown);

        return () => {
            document.removeEventListener('keydown', handleKeydown);
        };
    }, [dispatch, isFullscreenActive]);

    useUnmount(() => {
        dispatch(closeFullScreenStream());
    });

    useCssClassToggleOnDocument(
        CLASS_NAME_OF_DISABLED_SCROLL,
        isFullscreenActive
    );

    useCssClassToggleOnDocument(
        CLASS_NAME_OF_FULLSCREEN_THEATER,
        isFullscreenActive
    );

    const { medium, small } = useSelector((state: State) =>
        makeViewportSelector('lessThanOrEqual')(state, {})
    );

    const isSmallMedium = isFullscreenActive && medium && !small;

    useDebugValue({
        height,
        containerRef,
        isSmallMedium,
    });

    return {
        height,
        containerRef,
        isSmallMedium,
    };
}

export default useTheater;
